import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

import { api } from "@/lib/api";

/**
 * Generieke renderer voor DB-driven juridische pagina's
 * (privacy, algemene-voorwaarden, cookiebeleid).
 */
export default function LegalPage({ slug: forcedSlug }) {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError("");
    api.get(`/legal-pages/${slug}`)
      .then((r) => { if (!cancelled) setPage(r.data); })
      .catch(() => { if (!cancelled) setError("Pagina niet gevonden"); });
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-3xl font-bold text-ink">{error}</h1>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="h-8 w-1/2 bg-slate-200 rounded animate-pulse" />
        <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse mt-4" />
        <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse mt-2" />
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-6 py-20 prose prose-slate" data-testid={`legal-page-${slug}`}>
      <Helmet>
        <title>{`${page.title} — CheckExpert`}</title>
        <meta name="description" content={(page.intro || "").slice(0, 160)} />
      </Helmet>
      <div className="mb-10">
        {page.eyebrow && (
          <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">{page.eyebrow}</span>
        )}
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink mt-3 tracking-tight">{page.title}</h1>
        {(page.last_updated_label || page.updated_at) && (
          <p className="text-ink-soft mt-3 text-sm">
            {page.last_updated_label || `Laatst bijgewerkt: ${page.updated_at.slice(0, 10)}`}
          </p>
        )}
        {page.intro && (
          <p className="mt-6 text-base sm:text-lg text-ink leading-relaxed not-prose">{page.intro}</p>
        )}
      </div>

      <div className="space-y-10 text-ink-soft leading-relaxed">
        {(page.sections || []).map((s, i) => (
          <section key={i} data-testid={`legal-section-${i}`}>
            <h2 className="font-display text-2xl font-semibold text-ink">{s.heading}</h2>
            <div
              className="mt-3 prose prose-slate prose-sm sm:prose-base max-w-none [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-ink [&_h3]:mt-5 [&_h3]:mb-2 [&_ul]:mt-2 [&_p]:mt-2"
              dangerouslySetInnerHTML={{ __html: s.body_html }}
            />
          </section>
        ))}
      </div>
    </article>
  );
}
